import { Component, effect, inject } from '@angular/core';
import { DataLayerType } from '../../../models/data-layer-type';
import { GlobalsStore } from '../../../stores/globals.state';
import { DataLayerTypeStore } from '../../../stores/data-layer-type/data-layer-type.state';
import { getState } from '@ngrx/signals';
import { DataLayer } from '../../../models/data-layer';
import { TabViewModule } from 'primeng/tabview';
import { DataLayerTypeEntryComponent } from '../../molecules/data-layer-type-entry/data-layer-type-entry.component';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { DividerModule } from 'primeng/divider';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SearchInputComponent } from '../../atoms/search-input/search-input.component';
import { StateFilter } from '../../../stores/crud.state';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MessageService } from 'primeng/api';
import { BaseListComponent } from '../base-list/base-list.component';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { RoutingService } from '../../../services/routing.service';
import { HeaderComponent } from '../../atoms/header/header.component';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { DataLayerFormComponent } from '../data-layer-form/data-layer-form.component';
import { StyleClassModule } from 'primeng/styleclass';
import { AddClassToTabPanelsDirective } from '../../../directives/add-class-to-tab-panels.directive';
import { Button } from 'primeng/button';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { TooltipModule } from 'primeng/tooltip';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { PermissionStore } from '../../../stores/permission.state';

@Component({
  selector: 'app-data-layer-type-administration',
  standalone: true,
  imports: [
    TabViewModule,
    DataLayerTypeEntryComponent,
    NgIf,
    DividerModule,
    ScrollPanelModule,
    SearchInputComponent,
    CdkDropList,
    CdkDrag,
    NgForOf,
    NgClass,
    HeaderComponent,
    MaskComponent,
    RouterOutlet,
    SidebarModule,
    DataLayerFormComponent,
    StyleClassModule,
    AddClassToTabPanelsDirective,
    Button,
    AlertComponent,
    RouterLink,
    TooltipModule,
  ],
  templateUrl: './data-layer-type-administration.component.html',
  styleUrl: './data-layer-type-administration.component.scss',
})
export class DataLayerTypeAdministrationComponent extends BaseListComponent<DataLayerType> {
  override sidebarId = 'datalayer-administration';
  override stepsNavigateBack = 2;

  readonly permissionStore = inject(PermissionStore);
  readonly globalStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly store = inject(DataLayerTypeStore);
  readonly dataLayerStore = inject(DataLayerStore);

  activeTabIndex = 0;
  selectedType: DataLayerType | null = null;
  selectedLayer: DataLayer | null = null;
  groupedDataLayerTypes?: { [type: string]: DataLayerType[] };
  filterQuery: StateFilter = {
    query: '',
    category: [],
    locked: null,
    published: null,
    tags: [],
    products: [],
    ids: undefined,
  };
  addDataLayerForm = false;
  addToParent: DataLayerType | undefined;

  constructor(
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );
    effect(() => {
      const globalState = getState(this.globalStore);
      const dataLayerTypeState = getState(this.store);

      this.selectedType = globalState.selectedType;
      this.selectedLayer = globalState.selectedLayer;
      if (dataLayerTypeState.initialized) {
        this.groupedDataLayerTypes = this.store.groupedByType();
      }
    });
  }

  /**
   * Filters the existing types based on the provided search term.
   *
   * @param {string} searchTerm - The search term to filter the types.
   * @return {void} This method does not return a value.
   */
  filterTypes(searchTerm: string): void {
    this.filterQuery.query = searchTerm;
    this.updateFilter();
  }

  /**
   * Updates the filter in the store with the current filter query after a short delay.
   * This ensures any ongoing processes are completed before updating the filter.
   *
   * @return {void} This method does not return any value.
   */
  updateFilter(): void {
    setTimeout(() => {
      this.store.updateFilter(this.filterQuery);
    }, 10);
  }

  /**
   * Handles the drop event to reorder items in a draggable list and updates their sorting index.
   *
   * @param {CdkDragDrop<string[]>} event - The drag-and-drop event containing information about the item and its previous and current positions.
   * @return {void} This method does not return any value.
   */
  drop(event: CdkDragDrop<string[]>): void {
    const item = event.item.data;
    if (!this.groupedDataLayerTypes || !this.groupedDataLayerTypes[item.type])
      return;

    moveItemInArray(
      this.groupedDataLayerTypes[item.type],
      event.previousIndex,
      event.currentIndex
    );

    this.groupedDataLayerTypes[item.type].map(
      (dataLayerType: DataLayerType, index: number) => {
        dataLayerType.sorting = index;
      }
    );

    this.store.updateMultiple({ items: this.groupedDataLayerTypes[item.type] });
  }

  /**
   * Adds a year to the specified parent data layer and updates the form state.
   *
   * @param {DataLayerType} parent - The parent data layer to which the year will be added.
   * @return {void} Does not return any value.
   */
  addYear(parent: DataLayerType): void {
    this.addToParent = parent;
    if (this.permissionStore) this.addDataLayerForm = true;
  }

  /**
   * Closes the data layer form by resetting related properties.
   *
   * This method sets the `addToParent` property to undefined and
   * the `addDataLayerForm` property to false, ensuring that the
   * form is no longer active or displayed.
   *
   * @return {void} Does not return a value.
   */
  closeDataLayerForm(): void {
    this.addToParent = undefined;
    this.addDataLayerForm = false;
  }

  /**
   * Adds a new layer type and redirects to the appropriate route based on the active tab index.
   * If the active tab index is 0, it redirects to the route for "raum neu".
   * If the active tab index is not 0, it redirects to the route for "poi neu".
   *
   * @return {void} This method does not return a value.
   */
  addLayerType(): void {
    const route =
      this.activeTabIndex === 0
        ? [
            '/data-backend',
            {
              outlets: {
                overlay: ['neu', { outlets: { panel: ['raum', 'neu'] } }],
              },
            },
          ]
        : [
            '/data-backend',
            {
              outlets: {
                overlay: ['neu', { outlets: { panel: ['poi', 'neu'] } }],
              },
            },
          ];
    this.routingService.redirectToRoute(route);
  }
}
